import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { conferenceRooms } from '@/api/conferenceRooms';
import type { ConferenceRoomRow, ConferenceMode, CreateRoomBody } from '@/api/conferenceRooms';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useEffectiveCustomerId } from '@/lib/customer-context-store';
import { useCustomerPicker } from '@/components/CustomerPicker';
import { CustomerScopeChip } from '@/components/CustomerScopeChip';
import { formatRef } from '@/lib/formatRef';
import { PageHeader } from '@/components/PageHeader';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Plus, MoreHorizontal, Trash2, Pencil, Users, Settings2, Radio, History } from 'lucide-react';

const CAN_MUTATE = ['super_admin', 'reseller_owner', 'office_manager'];

export function ConferenceRoomsList() {
  const user = useCurrentUser();
  const effectiveCustomerId = useEffectiveCustomerId();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canMutate = CAN_MUTATE.includes(user.role);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ConferenceRoomRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ConferenceRoomRow | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['conferenceRooms', effectiveCustomerId],
    queryFn: () => conferenceRooms.list({ limit: 200, customer_id: effectiveCustomerId ?? undefined }),
  });
  const rows = data?.rows ?? [];

  const remove = useMutation({
    mutationFn: (id: string) => conferenceRooms.remove(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['conferenceRooms'] }); setDeleteTarget(null); },
  });

  const columns = [
    { header: 'Ref', cell: (r: ConferenceRoomRow) => <span className="font-mono text-xs text-slate-500">{formatRef(r.ref_number, 'CONF')}</span> },
    { header: 'Name', cell: (r: ConferenceRoomRow) => <span className="text-sm font-medium">{r.name}</span> },
    { header: 'Mode', cell: (r: ConferenceRoomRow) => (
      <Badge variant="outline" className="text-[10px] capitalize">{r.mode}</Badge>
    ) },
    { header: 'PINs', cell: (r: ConferenceRoomRow) => (
      <div className="flex gap-1">
        {r.has_admin_pin && <Badge variant="outline" className="text-[10px]">Admin</Badge>}
        {r.has_participant_pin ? (
          <Badge variant="outline" className="text-[10px]">Participant</Badge>
        ) : (
          <Badge variant="secondary" className="text-[10px]">No guest PIN</Badge>
        )}
      </div>
    ) },
    { header: 'Capacity', cell: (r: ConferenceRoomRow) => (
      <span className="text-xs font-mono tabular-nums text-slate-500">{r.max_participants}</span>
    ) },
    { header: 'Active', cell: (r: ConferenceRoomRow) => (
      r.active ? <Badge variant="default">Active</Badge> : <Badge variant="secondary">Inactive</Badge>
    ) },
    { header: '', className: 'w-10 text-end', cell: (r: ConferenceRoomRow) => (
      canMutate ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              aria-label="Row actions"
              onClick={(e) => e.stopPropagation()}
              className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            >
              <MoreHorizontal className="h-4 w-4" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuItem onClick={() => navigate(`/conferences/${r.id}/live`)}>
              <Radio className="h-3.5 w-3.5 mr-2" /> Live dashboard
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate(`/conferences/${r.id}/history`)}>
              <History className="h-3.5 w-3.5 mr-2" /> History
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => setEditTarget(r)}>
              <Pencil className="h-3.5 w-3.5 mr-2" /> Quick edit
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate(`/conferences/${r.id}`)}>
              <Settings2 className="h-3.5 w-3.5 mr-2" /> Advanced options
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-red-600 focus:text-red-600 focus:bg-red-50"
              onClick={() => setDeleteTarget(r)}
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null
    ) },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Telephony"
        title="Conference rooms"
        description="Audio conferencing rooms callers dial into from an IVR conference node. PINs gate entry — admins become moderators, participants join muted by default."
        actions={canMutate && (
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New room
          </Button>
        )}
      />

      {isLoading ? (
        <div className="text-sm text-slate-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-12 text-center">
          <Users className="mx-auto h-8 w-8 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-500">No conference rooms yet</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto">
            Create a room, then add a <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">conference</code> node
            to your IVR tree and set its <code className="rounded bg-slate-100 px-1 py-0.5 text-[11px]">conference_room_id</code> to this room&rsquo;s id.
          </p>
          {canMutate && (
            <Button size="sm" className="mt-4" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4 mr-1.5" /> Create the first room
            </Button>
          )}
        </div>
      ) : (
        <DataTable rows={rows} columns={columns} empty="" />
      )}

      {createOpen && <CreateRoomDialog onClose={() => setCreateOpen(false)} />}
      {editTarget && <EditRoomDialog room={editTarget} onClose={() => setEditTarget(null)} />}
      {deleteTarget && (
        <Dialog open onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete conference room?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-slate-600">
              <span className="font-medium">{deleteTarget.name}</span> ({formatRef(deleteTarget.ref_number, 'CONF')})
              will be soft-deleted. Any IVR node pointing at it will get a <code>conference_not_found</code>{' '}
              branch until you reconfigure the node.
            </p>
            <DialogFooter>
              <DialogClose asChild><Button type="button" variant="outline" disabled={remove.isPending}>Cancel</Button></DialogClose>
              <Button variant="destructive" disabled={remove.isPending} onClick={() => remove.mutate(deleteTarget.id)}>
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}

// ── Create dialog ─────────────────────────────────────────────────────────────

function CreateRoomDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [adminPin, setAdminPin] = useState('');
  const [participantPin, setParticipantPin] = useState('');
  const [mode, setMode] = useState<ConferenceMode>('open');
  const [maxParticipants, setMaxParticipants] = useState(50);
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { effectiveCustomerId, element: customerPicker, isPickerVisible } =
    useCustomerPicker(customerId, setCustomerId, { required: true });

  const create = useMutation({
    mutationFn: (body: CreateRoomBody) => conferenceRooms.create(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conferenceRooms'] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Create failed.'),
  });

  function submit() {
    setError(null);
    if (!name.trim()) { setError('Name required.'); return; }
    if (adminPin.length < 3) { setError('Admin PIN must be at least 3 digits.'); return; }
    if (effectiveCustomerId === null) { setError('Customer required.'); return; }
    create.mutate({
      name: name.trim(),
      admin_pin: adminPin,
      participant_pin: participantPin || null,
      mode,
      max_participants: maxParticipants,
      ...(effectiveCustomerId
        ? { customer_id: effectiveCustomerId }
        : {}),
    });
  }

  const canSubmit = name.trim().length > 0
    && adminPin.length >= 3
    && effectiveCustomerId !== null;

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New conference room</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {customerPicker}
          <CustomerScopeChip />
          <div className="space-y-1">
            <Label htmlFor="cr-name">Name</Label>
            <Input id="cr-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Weekly team call" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="cr-admin-pin">Admin PIN (moderator)</Label>
              <Input id="cr-admin-pin" type="password" autoComplete="off"
                value={adminPin}
                onChange={(e) => setAdminPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="e.g. 4812" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="cr-part-pin">Participant PIN (optional)</Label>
              <Input id="cr-part-pin" type="password" autoComplete="off"
                value={participantPin}
                onChange={(e) => setParticipantPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="Leave blank to lock to admins" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Mode</Label>
              <Select value={mode} onValueChange={(v) => setMode(v as ConferenceMode)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="moderated">Moderated</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="cr-max">Max participants</Label>
              <Input id="cr-max" type="number" min={2} max={500}
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(parseInt(e.target.value, 10) || 50)} />
            </div>
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline" disabled={create.isPending}>Cancel</Button></DialogClose>
          <Button type="button" onClick={submit} disabled={create.isPending || !canSubmit}>
            {create.isPending ? 'Creating…' : 'Create room'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit dialog ───────────────────────────────────────────────────────────────

function EditRoomDialog({ room, onClose }: { room: ConferenceRoomRow; onClose: () => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(room.name);
  const [newAdminPin, setNewAdminPin] = useState('');
  const [newParticipantPin, setNewParticipantPin] = useState('');
  const [clearParticipantPin, setClearParticipantPin] = useState(false);
  const [mode, setMode] = useState<ConferenceMode>(room.mode);
  const [maxParticipants, setMaxParticipants] = useState(room.max_participants);
  const [active, setActive] = useState(room.active);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => conferenceRooms.update(room.id, {
      name: name.trim() || undefined,
      admin_pin: newAdminPin || undefined,
      participant_pin: clearParticipantPin ? null : (newParticipantPin || undefined),
      mode,
      max_participants: maxParticipants,
      active,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['conferenceRooms'] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Save failed.'),
  });

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Edit {room.name}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1">
            <Label htmlFor="er-name">Name</Label>
            <Input id="er-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="er-admin-pin">New admin PIN</Label>
              <Input id="er-admin-pin" type="password" autoComplete="off"
                value={newAdminPin}
                onChange={(e) => setNewAdminPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="Leave blank to keep existing" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="er-part-pin">New participant PIN</Label>
              <Input id="er-part-pin" type="password" autoComplete="off"
                disabled={clearParticipantPin}
                value={newParticipantPin}
                onChange={(e) => setNewParticipantPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder={room.has_participant_pin ? 'Keep existing (blank)' : 'Set a guest PIN'} />
              {room.has_participant_pin && (
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer pt-1">
                  <input type="checkbox" checked={clearParticipantPin}
                    onChange={(e) => setClearParticipantPin(e.target.checked)}
                    className="accent-primary" />
                  Clear participant PIN (lock to admins only)
                </label>
              )}
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Mode</Label>
              <Select value={mode} onValueChange={(v) => setMode(v as ConferenceMode)}>
                <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="open">Open</SelectItem>
                  <SelectItem value="moderated">Moderated</SelectItem>
                  <SelectItem value="private">Private</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="er-max">Max participants</Label>
              <Input id="er-max" type="number" min={2} max={500}
                value={maxParticipants}
                onChange={(e) => setMaxParticipants(parseInt(e.target.value, 10) || 50)} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="accent-primary" />
            Active (inactive rooms reject all join attempts)
          </label>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button type="button" variant="outline" disabled={save.isPending}>Cancel</Button></DialogClose>
          <Button type="button" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
